/**
 * The reader workspace (web.md §4, §6, §8, §9; design/MECHANICS.md "Reader interactions", "Saved
 * Views"): the message list, the reader and Details as siblings inside `.workspace-content`, the
 * reader's tool islands in the top bar, its shortcuts and its dialogs (View builder, list filters,
 * topics, Briefing).
 *
 * Selection lives in the route (`/f/:folder/m/:message`, `/v/…`, `/corrections/…`); with no message in
 * the route the first row is shown (the prototype's start state) without being opened. On phones the
 * route decides the screen, and Details is a history entry (`?details=1`) so the browser's Back works.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { FolderId, MessageId, MessageRow } from '@digester/core';
import { registerDialog } from '../app/dialogs.js';
import { openDialog } from '../app/dialogs.js';
import { useLocalSettings, useSettings, useViews } from '../app/hooks.js';
import { messagePath, scopePath, useReaderRoute } from '../app/reader-route.js';
import { useShortcut } from '../app/shortcuts.js';
import { reportError } from '../app/errors.js';
import { showToast } from '../app/Toast.js';
import { isPhone } from '../app/Topbar.js';
import { TopbarActions } from '../app/topbar-slot.js';
import { DEFAULT_LIST_FILTER, ui, useUi } from '../app/ui-store.js';
import { useEngine, useEngineQuery } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { Tool } from '../ui/Tool.js';
import { useReaderActions } from './actions.js';
import { BriefingDialog } from './BriefingDialog.js';
import { CopyMarkdownDialog } from './CopyMarkdownDialog.js';
import { buildListQuery, rowFromDetail, useCorrectionsList, useLookups, useMessageList, type ReaderList } from './data.js';
import { DetailsPane } from './DetailsPane.js';
import { settingsPath } from './links.js';
import { ListFilterDialog } from './ListFilterDialog.js';
import { MessageList } from './MessageList.js';
import { ReaderPane } from './ReaderPane.js';
import { scrollKey } from './scroll-memory.js';
import { TopicsDialog } from './TopicsDialog.js';
import { ViewBuilder } from './ViewBuilder.js';
import './reader.css';

registerDialog('view-builder', ViewBuilder);
registerDialog('list-filter', ListFilterDialog);
registerDialog('topics', TopicsDialog);
registerDialog('briefing', BriefingDialog);
registerDialog('copy-markdown', CopyMarkdownDialog);

/** True when the reader was entered from the list in this tab (Back can pop it). */
let openedFromList = false;

export function ReaderScreen() {
  const route = useReaderRoute();
  const navigate = useNavigate();
  const location = useLocation();
  const engine = useEngine();
  const qc = useQueryClient();
  const lookups = useLookups();
  const views = useViews();
  const settings = useSettings();
  const local = useLocalSettings();
  const search = useUi((s) => s.search);
  const listFilter = useUi((s) => s.listFilter);
  const detailsOpen = useUi((s) => s.detailsOpen);
  const overlay = useUi((s) => s.mobileOverlay);
  const actions = useReaderActions(lookups);
  const [selectionTick, setSelectionTick] = useState(0);

  const scope = route.scope;
  const path = scopePath(scope);
  const isCorrections = scope.kind === 'corrections';
  const timeZone = settings?.profile.timezone;
  const showImages = settings?.reading.showImages ?? true;

  // ── The list ──────────────────────────────────────────────────────────────────────────────────
  const query = useMemo(
    () => buildListQuery(scope.kind === 'corrections' ? { kind: 'folder', folderId: 'inbox' as FolderId } : scope, search, listFilter, route.messageId),
    [scope, search, listFilter, route.messageId],
  );
  const folderList = useMessageList(query, !isCorrections);
  const corrections = useCorrectionsList(isCorrections, lookups.filters, search);
  const list: ReaderList = isCorrections ? corrections : folderList;
  const rows = list.rows;

  // ── Selection ─────────────────────────────────────────────────────────────────────────────────
  const selectedId: MessageId | null = route.messageId ?? rows[0]?.id ?? null;
  const detailQuery = useEngineQuery(qk.message(selectedId ?? ('' as MessageId)), (e) => e.messages.get(selectedId as MessageId), { enabled: selectedId !== null });
  const detail = selectedId && detailQuery.data?.message.id === selectedId ? detailQuery.data : undefined;
  const selected: MessageRow | null = useMemo(() => rows.find((r) => r.id === selectedId) ?? (detail ? rowFromDetail(detail, lookups.filters) : null), [rows, selectedId, detail, lookups.filters]);
  const index = selected ? rows.findIndex((r) => r.id === selected.id) : -1;

  // Prefetch the next message's detail (J feels instant).
  const nextRow = index >= 0 ? rows[index + 1] : undefined;
  useEffect(() => {
    if (!nextRow) return;
    void qc.prefetchQuery({ queryKey: qk.message(nextRow.id), queryFn: () => engine.messages.get(nextRow.id) });
  }, [nextRow?.id, qc, engine]);

  // Read on open (Reading → "Mark read on open"): once per explicitly opened message.
  const autoRead = useRef<string | null>(null);
  useEffect(() => {
    const id = route.messageId;
    if (!id || autoRead.current === id || !settings) return;
    const row = rows.find((r) => r.id === id) ?? selected;
    if (!row) return;
    autoRead.current = id;
    if (settings.reading.readOnOpen && !row.read) void actions.setRead(row, true, { quiet: true });
  }, [route.messageId, rows, selected, settings]);

  // Opening from the list pushes one history entry; moving between messages replaces it, so Back
  // returns to the list (phones) or to where the reader was entered (desktop).
  const open = useCallback(
    (id: MessageId) => {
      setSelectionTick((t) => t + 1);
      if (!route.messageId) openedFromList = true;
      navigate(messagePath(scope, id), { replace: route.messageId !== null });
    },
    [navigate, scope, route.messageId],
  );

  const step = (delta: 1 | -1): void => {
    if (!rows.length) return;
    const i = index < 0 ? (delta > 0 ? -1 : rows.length) : index;
    const target = rows[i + delta];
    if (target) open(target.id);
    else {
      if (delta > 0 && list.hasNextPage) list.fetchNextPage();
      showToast(delta > 0 ? 'Last message' : 'First message');
    }
  };

  const backToList = (): void => {
    if (openedFromList && ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0) {
      openedFromList = false;
      navigate(-1);
    } else navigate(path, { replace: true });
  };

  // ── Details (desktop toggle; phone = a history entry) ─────────────────────────────────────────
  const detailsParam = new URLSearchParams(location.search).has('details');
  useEffect(() => {
    // After the Workspace's own route effect (which resets overlays on a new path).
    let live = true;
    void Promise.resolve().then(() => {
      if (!live) return;
      const want = detailsParam && isPhone() ? 'details' : 'none';
      if (ui.get().mobileOverlay !== 'navigation' || want === 'details') ui.set({ mobileOverlay: want });
    });
    return () => {
      live = false;
    };
  }, [detailsParam, location.key]);

  const toggleDetails = (): void => {
    if (isPhone()) {
      if (detailsParam && ui.get().mobileOverlay === 'details') navigate(-1);
      else if (detailsParam) ui.set({ mobileOverlay: 'details' }); // closed with Escape: the entry is still there
      else if (selected) navigate({ pathname: location.pathname, search: '?details=1' });
      return;
    }
    const next = !ui.get().detailsOpen;
    ui.set({ detailsOpen: next });
    if (local) void engine.settings.setLocal('ui', { ...local.ui, detailsOpen: next }).catch(() => undefined);
  };

  const go = (to: string): void => navigate(to);

  // ── Moves (desktop: on to the neighbour; phone: back to the list, like the prototype) ────────
  const move = (folderId: FolderId): void => {
    if (!selected || selected.folderId === folderId) return;
    const leaves = scope.kind === 'folder' || scope.kind === 'corrections';
    const neighbour = index >= 0 ? rows[index + 1] ?? rows[index - 1] : undefined;
    void actions.move(selected, folderId);
    if (isPhone()) {
      if (route.messageId) backToList();
      return;
    }
    if (route.messageId && leaves && scope.kind === 'folder') {
      if (neighbour) navigate(messagePath(scope, neighbour.id), { replace: true });
      else navigate(path, { replace: true });
    }
  };

  const toggleRead = (): void => {
    if (selected) void actions.setRead(selected, !selected.read);
  };

  // "Score now" (models.md §12): asks every switched-on model that has the "I ask" trigger.
  const canScore = lookups.models.some((m) => m.enabled && m.entitled && m.run.triggers.some((t) => t.on === 'request'));
  const scoreNow = async (): Promise<void> => {
    if (!selected) return;
    try {
      const result = await engine.messages.rescore([selected.id]);
      showToast(result.changed > 0 ? 'Scoring requested · the scores arrive in Details' : 'No model scores this message on request');
    } catch (error) {
      reportError(error);
    }
  };

  const folders = lookups.folders;
  const hasSelection = selected !== null;

  // ── Shortcuts (web.md §6) ─────────────────────────────────────────────────────────────────────
  useShortcut('j', () => step(1), { description: 'Next message' });
  useShortcut('k', () => step(-1), { description: 'Previous message' });
  useShortcut('t', () => selected && actions.pickTopics(selected), { description: 'Topics and tags' });
  useShortcut('c', () => selected && void actions.copyMarkdown(selected), { description: 'Copy as Markdown' });
  useShortcut('m', toggleRead, { description: 'Read / unread' });
  useShortcut('i', toggleDetails, { description: 'Toggle Details' });
  useShortcut('z', () => void actions.undoLast(), { description: 'Undo' });
  for (const n of [1, 2, 3, 4, 5, 6, 7, 8, 9]) {
    // A fixed list of nine keys: the hook order never changes.
    useShortcut(String(n), () => {
      const f = folders[n - 1];
      if (f) move(f.id);
    }, { description: n === 1 ? 'Move to the folder at that position (1–9)' : `Move to folder ${n}`, when: () => folders.length >= n });
  }

  // ── Titles and empty states ──────────────────────────────────────────────────────────────────
  const view = scope.kind === 'view' ? views.find((v) => v.id === scope.viewId) : undefined;
  const title = scope.kind === 'folder' ? lookups.folder(scope.folderId)?.name ?? 'Messages' : scope.kind === 'view' ? view?.name ?? 'View' : 'Corrections';
  const filtered = search.trim() !== '' || listFilter.unreadOnly || listFilter.sourceId !== null || listFilter.topicId !== null;
  const clearFilters = (): void => ui.set({ search: '', listFilter: DEFAULT_LIST_FILTER });
  const openBriefing = (): void => void openDialog('briefing', { query, scope, title, ...(timeZone ? { timeZone } : {}) });

  const headingTool = view ? <Tool icon="settings" label="Edit view" onClick={() => openDialog('view-builder', { viewId: view.id })} /> : <Tool icon="newspaper" label="Briefing" data-mobile-brief="true" onClick={openBriefing} />;

  return (
    <>
      <TopbarActions>
        <div className="tool-island folder-tools" aria-label="Move message to folder">
          {folders.slice(0, 9).map((f, i) => (
            <Tool key={f.id} icon={f.icon} label={`Move to ${f.name} · ${i + 1}`} data-folder={f.id} pressed={selected?.folderId === f.id} disabled={!hasSelection} onClick={() => move(f.id)} />
          ))}
        </div>
        <div className="tool-island">
          <Tool icon="tag" label="Pick topics · T" disabled={!hasSelection} onClick={() => selected && actions.pickTopics(selected)} />
          <Tool icon="copy" label="Copy Markdown · C" disabled={!hasSelection} onClick={() => selected && void actions.copyMarkdown(selected)} />
          <Tool icon={selected?.read ? 'eye-off' : 'eye'} label={selected?.read ? 'Mark unread · M' : 'Mark read · M'} data-action="read" disabled={!hasSelection} onClick={toggleRead} />
          {canScore ? <Tool icon="gauge" label="Score now" data-action="score-now" disabled={!hasSelection} onClick={() => void scoreNow()} /> : null}
        </div>
        <div className="tool-island supplementary">
          <Tool icon="newspaper" label="Briefing" onClick={openBriefing} />
          <Tool icon="sun" label="Appearance" onClick={() => navigate('/settings/theme/appearance')} />
        </div>
        <div className="toolbar-spacer" />
        <div className="tool-island detail-tool">
          <Tool icon="panel-right" label="Toggle details · I" pressed={isPhone() ? overlay === 'details' : detailsOpen} onClick={toggleDetails} />
        </div>
      </TopbarActions>

      <MessageList
        title={title}
        list={list}
        selectedId={selectedId}
        lookups={lookups}
        showImages={showImages}
        timeZone={timeZone}
        orderLabel={listFilter.sort === 'relevance' ? 'Relevance' : 'Newest first'}
        headingTool={headingTool}
        scrollKey={scrollKey('list', path, null)}
        selectionTick={selectionTick}
        empty={{ title: isCorrections && !filtered ? 'No open corrections' : filtered ? 'No results' : 'No messages', canClear: filtered, onClear: clearFilters }}
        {...(isCorrections ? { gone: corrections.gone } : {})}
        onOpen={(id) => open(id)}
      />

      <ReaderPane
        row={selected}
        detail={detail}
        detailError={detailQuery.error ?? null}
        lookups={lookups}
        showImages={showImages}
        timeZone={timeZone}
        scrollKey={scrollKey('reader', path, selectedId)}
        onBack={backToList}
        onDetails={toggleDetails}
        onPrevious={() => step(-1)}
        onNext={() => step(1)}
        onOpenSource={(id) => go(settingsPath.source(id))}
      />

      <DetailsPane row={selected} detail={detail} lookups={lookups} timeZone={timeZone} scrollKey={scrollKey('details', path, selectedId)} onClose={toggleDetails} go={go} />
    </>
  );
}
